import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, ResponsiveTable } from "../../components/dash/ui";
import { Button } from "../../components/ui/button";
import { formatRupiah, isManagerRole } from "../../lib/labels";
import { Pill, CONTRACT_STATUS, SCHEME_NAMES, Disclaimer } from "./shared";
import { ContractForm } from "./ContractForm";

export default function ProfitList() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = () => api.get("/profit/contracts").then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => {
    load();
    api.get("/profit/schemes").then((r) => setMeta(r.data));
  }, []);
  const farmer = isManagerRole(user.role);
  const term = (r, k) => (r.status === "LOCKED" ? r[k] : r.proposal?.terms?.[k]);
  const columns = [
    { key: "projectCode", label: "Kebun", render: (r) => <span><b>{r.projectCode}</b><br /><span className="text-xs text-slate-500">{r.projectName}</span></span> },
    { key: "farmerGroupName", label: "Pengelola", render: (r) => <span>{r.farmerGroupName}<br /><span className="text-xs text-slate-500">{r.managerKind}</span></span> },
    { key: "investorName", label: "Investor" },
    { key: "capitalAmount", label: "Modal", render: (r) => term(r, "capitalAmount") ? formatRupiah(term(r, "capitalAmount")) : "-" },
    { key: "scheme", label: "Skema", render: (r) => `${SCHEME_NAMES[term(r, "scheme")] || "-"}${r.version ? ` · v${r.version}` : ""}` },
    { key: "status", label: "Status", render: (r) => <span className="flex flex-col gap-1"><Pill map={CONTRACT_STATUS} value={r.status} testid={`contract-status-${r.id}`} />
      {r.status === "LOCKED" && r.proposal && r.proposal.status !== "CANCELLED" && <span className="text-xs text-blue-700" data-testid={`amendment-status-${r.id}`}>Amendment: {CONTRACT_STATUS[r.proposal.status]?.[0]}</span>}</span> },
  ];
  const title = { FARMER_GROUP: "Skema & Bagi Hasil Kebun", INDIVIDUAL_MANAGER: "Skema & Bagi Hasil Kebun", INVESTOR: "Bagi Hasil Saya", SUPER_ADMIN: "Review Skema & Bagi Hasil Kebun" }[user.role];
  const sub = { FARMER_GROUP: "Input skema hasil kesepakatan dengan Investor, kirim untuk persetujuan, lalu catat realisasi setelah PT SJI mengunci skema.",
    INDIVIDUAL_MANAGER: "Porsi Pengelola pada skema kebun Anda diterima oleh Anda sebagai Pengelola Perorangan sesuai kontrak. Input skema, kirim untuk persetujuan, lalu catat realisasi setelah PT SJI mengunci skema.",
    INVESTOR: "Tinjau dan setujui skema kebun Anda, pantau progres BEP, penjualan, bagian Anda, dan bukti pembayaran.",
    SUPER_ADMIN: "PT SJI memeriksa kesesuaian data dengan dokumen, meminta revisi, dan mengunci skema yang sudah disetujui para pihak. PT SJI tidak menentukan persentase." }[user.role];
  return (
    <div data-testid="profit-list-page">
      <PageHeader title={title} subtitle={sub}
        action={farmer && !creating && <Button onClick={() => setCreating(true)} className="bg-emerald-800 hover:bg-emerald-900" data-testid="new-contract-btn"><Plus className="h-4 w-4 mr-1" />Buat Skema Bagi Hasil</Button>} />
      {meta && <div className="mb-6"><Disclaimer text={meta.disclaimer} /></div>}
      {creating && <div className="mb-6"><ContractForm masters={meta?.schemes} onDone={() => { setCreating(false); load(); }} onCancel={() => setCreating(false)} /></div>}
      <ResponsiveTable columns={columns} rows={rows} testid="contracts-table" empty="Belum ada skema bagi hasil."
        actions={(r, p) => <Link to={r.id} className="text-sm font-semibold text-emerald-800 hover:underline" data-testid={`${p}open-contract-${r.id}`}>Buka</Link>} />
    </div>
  );
}
